import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { Theme } from '@app/theme';
import { useDrop } from 'react-dnd';

import {
  useDropMethod,
  getDropAcceptTypes,
} from 'lib/participationDragAndDrop';
import { Trans } from 'react-i18next';
import { ParticipationStatus, StudyType } from '@app/api';
import { ParticipantMonitoringCard } from './ParticipantMonitoringCard';

const VISIBLE_HEIGHT = 48;
const TOTAL_HEIGHT = 150;
const OVER_VISIBLE_HEIGHT = 100;
const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {
    position: 'fixed',
    bottom: 0,
    right: 0,
    left: 0,
    height: 0,
  },
  dropzone: {
    width: 280,
    margin: 'auto',
    height: TOTAL_HEIGHT,
    borderRadius: theme.borderRadius.std,
    transform: `translateY(${VISIBLE_HEIGHT}px)`,
    transition: [
      ['transform', '.3s'],
      ['background-color', '.3s'],
    ],

    backgroundColor: theme.colors.redMuted,
    color: theme.colors.redAccessible,
    padding: [[0, theme.marginBase * 2]],
  },
  dopzoneText: {
    ...theme.fonts.label,
    color: undefined,

    height: VISIBLE_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  visible: {
    transform: `translateY(${-VISIBLE_HEIGHT}px)`,
  },
  isOver: {
    transform: `translateY(${-OVER_VISIBLE_HEIGHT}px)`,
    backgroundColor: theme.colors.red,
    color: theme.colors.white,
  },
  icon: {
    marginRight: theme.marginBase,
    fontSize: theme.iconSize.regular,
  },
}));

interface Props {
  onDragToCancel: (id: string) => any;
  studyType: StudyType;
}

export const RefusedDropZone = (props: Props) => {
  const status: ParticipationStatus[] = [
    ParticipationStatus.DECLINED,
    ParticipationStatus.CANCELED,
  ];

  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const { onDragToCancel } = props;
  const dropMethod = useDropMethod(status, props.studyType, { onDragToCancel });

  const accept = getDropAcceptTypes(status, props.studyType);
  const [dropInfo, drop] = useDrop({
    accept,
    drop: (item: any) => {
      dropMethod(item);
      return { status, specialDropZone: true };
    },
    collect: (mon) => ({
      isOver: !!mon.isOver(),
      canDrop: !!mon.canDrop(),
      itemType: mon.getItemType(),
      item: mon.getItem(),
      dropResult: mon.getDropResult(),
      didDrop: mon.didDrop(),
    }),
  });
  const dropedHere =
    dropInfo.didDrop &&
    dropInfo.dropResult.status === status &&
    dropInfo.dropResult.specialDropZone;
  const isOver = dropInfo.isOver || dropedHere;
  const canDrop = dropInfo.canDrop || dropedHere;

  return (
    <div className={classes.container}>
      <div
        ref={drop}
        className={classnames(classes.dropzone, {
          [classes.isOver]: isOver,
          [classes.visible]: canDrop,
        })}
      >
        <div className={classes.dopzoneText}>
          <i
            className={classnames(
              {
                'ri-close-circle-fill': isOver && canDrop,
                'ri-drag-drop-fill': !(isOver && canDrop),
              },
              classes.icon,
            )}
          />
          <Trans i18nKey="sessions.details.monitoring.decline" />
        </div>
        {isOver && canDrop && dropInfo.item && (
          <ParticipantMonitoringCard
            participation={dropInfo.item.participation}
            ghost
          />
        )}
      </div>
    </div>
  );
};
